import './applicantEditPersonalPage.scss';

import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import { ThemeProvider } from '@mui/material/styles';
import { PERSON_NAME_PATTERN } from '@thesavingsgroup/regex';
import isEmpty from 'lodash/isEmpty';
import { ReactNode, useEffect } from 'react';
import { FieldValues, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';

import { Button } from '../../../components/Button/Button';
import { Header } from '../../../components/Header/Header';
import { Input } from '../../../components/Input/Input';
import { NavigationPane } from '../../../components/NavigationPane/NavigationPane';
import { NumberInput } from '../../../components/NumberInput/NumberInput';
import useApplicantStore from '../../../store/applicantStore/useApplicantStore';
import useThemeStore from '../../../store/themeStore/useThemeStore';
import useUserStore from '../../../store/userStore/useUserStore';
import { locator } from '../../../testing/e2e-locator/locator';

export function Component(): ReactNode {
  const { createTheme } = useThemeStore();
  const theme = createTheme();
  const navigate = useNavigate();

  const { t } = useTranslation();
  const {
    clearUserUpdate,
    computed: {
      userFirstName,
      userLastName,
      userPhoneNumber,
      userEmailAddress,
    },
    isUpdatingUser,
    setUserFirstName,
    setUserLastName,
    userUpdate,
    userUpdateSuccess,
    userUpdateFailedMessage,
  } = useUserStore();

  const {
    computed: { reviewEditPathname, reviewEditProgress },
  } = useApplicantStore();

  const {
    register,
    handleSubmit,
    formState: { errors, isDirty, isValid },
  } = useForm({
    criteriaMode: 'firstError',
    defaultValues: {
      firstNameValue: userFirstName ?? '',
      lastNameValue: userLastName ?? '',
    },
    delayError: 200,
    mode: 'onTouched',
    shouldFocusError: false,
  });

  const firstNameValidation = {
    required: t('validation.firstName.required'),
    minLength: {
      value: 2,
      message: t('validation.firstName.minLength', { min: 2 }),
    },
    pattern: {
      value: PERSON_NAME_PATTERN,
      message: t('validation.firstName.invalid'),
    },
  };

  const lastNameValidation = {
    required: t('validation.lastName.required'),
    minLength: {
      value: 2,
      message: t('validation.lastName.minLength', { min: 2 }),
    },
    pattern: {
      value: PERSON_NAME_PATTERN,
      message: t('validation.lastName.invalid'),
    },
  };

  const finishEditing = (): void => {
    navigate(reviewEditPathname);
  };

  const formSubmit = async (values: FieldValues): Promise<void> => {
    const { firstNameValue, lastNameValue } = values;

    if (!firstNameValue || !lastNameValue) {
      return;
    }

    setUserFirstName(firstNameValue);
    setUserLastName(lastNameValue);

    if (isDirty) {
      await userUpdate();
    } else {
      finishEditing();
    }
  };

  useEffect(
    (): (() => void) => (): void => {
      clearUserUpdate();
    },
    [],
  );

  useEffect((): void => {
    if (userUpdateSuccess) {
      finishEditing();
    }
  }, [userUpdateSuccess]);

  return (
    <ThemeProvider theme={theme}>
      <Box className="applicant-edit-personal-page" sx={theme.pageLayout}>
        <Header
          progress={reviewEditProgress}
          showDrawerToggle
          onBackClick={finishEditing}
        />

        <main>
          <form
            data-testid={locator.pages.applicantEditPersonal.form.root}
            noValidate
            onSubmit={handleSubmit(formSubmit)}
          >
            <div className="form-content">
              <h1 data-testid={locator.pages.applicantEditPersonal.form.h1}>
                {t('ApplicantEditPersonalPage.title')}
              </h1>
              <h2 data-testid={locator.pages.applicantEditPersonal.form.h2}>
                {t('ApplicantEditPersonalPage.subtitle')}
              </h2>

              {!isEmpty(userUpdateFailedMessage) && (
                <div style={{ marginBottom: '2rem' }}>
                  <Alert
                    data-testid={
                      locator.pages.applicantEditPersonal.alert.error
                    }
                    icon={false}
                    severity="error"
                  >
                    {userUpdateFailedMessage}
                  </Alert>
                </div>
              )}

              <div style={{ marginBottom: '3rem' }}>
                <div className="inline-inputs">
                  <div className="input-half">
                    <Input
                      {...register('firstNameValue', firstNameValidation)}
                      data-testid={
                        locator.pages.applicantEditPersonal.form.firstNameInput
                      }
                      error={errors.firstNameValue}
                      errorTestId={
                        locator.pages.applicantEditPersonal.form
                          .firstNameInputError
                      }
                      label={t('ApplicantEditPersonalPage.firstName')}
                    />
                  </div>

                  <div className="input-half">
                    <Input
                      {...register('lastNameValue', lastNameValidation)}
                      data-testid={
                        locator.pages.applicantEditPersonal.form.lastNameInput
                      }
                      error={errors.lastNameValue}
                      errorTestId={
                        locator.pages.applicantEditPersonal.form
                          .lastNameInputError
                      }
                      label={t('ApplicantEditPersonalPage.lastName')}
                    />
                  </div>
                </div>

                <div style={{ marginTop: '1.5rem' }}>
                  <NumberInput
                    data-testid={
                      locator.pages.applicantEditPersonal.form
                        .phoneInputDisabled
                    }
                    label={t('ApplicantEditPersonalPage.phone')}
                    value={userPhoneNumber}
                    disabled
                    phone
                  />
                </div>

                <div style={{ marginTop: '1.5rem' }}>
                  <Input
                    data-testid={
                      locator.pages.applicantEditPersonal.form.emailAddressInput
                    }
                    label={t('ApplicantEditPersonalPage.email')}
                    style={{ pointerEvents: 'none' }}
                    value={userEmailAddress}
                    disabled
                  />
                </div>
              </div>

              <div style={{ marginBottom: '3rem' }}>
                <div className="inline-buttons">
                  <Button
                    data-testid={
                      locator.pages.applicantEditPersonal.form.cancelButton
                    }
                    variant="secondary"
                    fullWidth
                    onClick={finishEditing}
                  >
                    {t('ApplicantEditPersonalPage.cancel')}
                  </Button>

                  <Button
                    data-testid={
                      locator.pages.applicantEditPersonal.form.submitButton
                    }
                    disabled={!isValid}
                    loader={isUpdatingUser}
                    fullWidth
                    submit
                  >
                    {t('ApplicantEditPersonalPage.save')}
                  </Button>
                </div>
              </div>
            </div>
            <footer
              data-testid={locator.pages.applicantEditPersonal.footer.root}
            >
              {t('copyright', { year: new Date().getFullYear() })}
            </footer>
          </form>
        </main>
      </Box>

      <NavigationPane withDrawer withUserNameHeader />
    </ThemeProvider>
  );
}

Component.displayName = 'ApplicantEditPersonalPage';
